import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import get from 'lodash/get';
import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import NumberFormat from 'react-number-format';
import { ADMIN_NOTE_WALLET_BALANCES } from '../../pages/apollo_queries';

// The list's "Balance" column (the legacy called it "Funded") is the LIVE Sila balance of each note's wallet
// (TL, 2026-10-30), read AFTER the list in one batched query for the rows on screen — never one call per
// row and never the whole catalogue. A row the backend could not price shows "—" with its reason; it is
// never painted as $0 (the legacy's defect: a provider failure looked like an empty wallet).
const REASONS = {
  NOTE_NOT_FOUND: 'This note no longer exists',
  NO_WALLET: 'No wallet yet — no investor money has reached this note',
  LEGACY_UNMAPPED:
    'Wallet minted by the legacy and not adopted yet — run the note-wallet adoption',
  PROVIDER_ERROR: 'Sila did not answer for this wallet',
};

// `ids` = the ids of the rows the table is rendering right now (≤ 25, the backend's cap).
export function useNoteWalletBalances(ids) {
  const { data, loading, error } = useQuery(ADMIN_NOTE_WALLET_BALANCES, {
    variables: { investmentIds: ids },
    skip: !ids || ids.length === 0,
    fetchPolicy: 'network-only',
  });
  const byId = {};
  for (const entry of get(data, 'adminNoteWalletBalances', []) || []) {
    byId[entry.investmentId] = entry;
  }
  return { byId, loading, error };
}

export function NoteWalletBalanceCell({ entry, loading, error }) {
  if (loading && !entry) return <LoadingOutlined />;
  if (error && !entry) {
    return (
      <Tooltip title="The wallet balances could not be read">
        <span style={{ color: '#8c8c8c' }}>—</span>
      </Tooltip>
    );
  }
  if (!entry) return <span style={{ color: '#8c8c8c' }}>—</span>;
  if (entry.unavailable) {
    return (
      <Tooltip title={REASONS[entry.unavailable] || entry.unavailable}>
        <span style={{ color: '#8c8c8c', cursor: 'help' }}>—</span>
      </Tooltip>
    );
  }
  return (
    <NumberFormat
      displayType="text"
      value={entry.balance}
      thousandSeparator={true}
      decimalScale={2}
      fixedDecimalScale={true}
      prefix={'$'}
      renderText={(value) => <div>{value}</div>}
    />
  );
}

// The column itself, built per render from the batch the tab asked for. Keyed `balance` so a tab can
// swap it into a static column set.
export const balanceColumn = (balances) => ({
  title: 'Balance',
  key: 'balance',
  dataIndex: '_id',
  render: (id) => (
    <NoteWalletBalanceCell
      entry={balances.byId[id]}
      loading={balances.loading}
      error={balances.error}
    />
  ),
});
